/**
 * Wellness Signals API actions (WELLNESS_SIGNALS_PLAN.md §6.8).
 * Mirrors the store/actions/health.ts module shape: thin thunks over apiClient,
 * `{ success, payload }` passthrough, no redux state (per-screen local state owns it).
 */

import { apiGet, apiPost, apiProjectsPatch } from '../../utilities/apiClient';
import { getSessionIds } from './_sessionIds';

export type WellnessGate = {
  feature: { enabled: boolean; state: string; reasons: string[]; paid_signals?: boolean };
  viewer: {
    is_recipient: boolean;
    can_view: boolean;
    can_configure: boolean;
    /** Account administrators only — narrower than can_configure on purpose. */
    can_consent_on_behalf?: boolean;
  };
  /** Absent on older servers; treat undefined as "don't know", never as false. */
  recipient?: { has_signed_in: boolean; is_member: boolean };
};

export type WellnessVitalRow = {
  key: string;
  label: string;
  state: 'confirmed' | 'pending' | 'overdue' | 'source_offline';
  [k: string]: any;
};

export type WellnessRollup = {
  status: 'active_now' | 'active_recently' | 'quiet_expected' | 'unusually_quiet' | 'signals_offline' | 'paused';
  headline: string;
  last_activity_at: string | null;
  last_activity_type?: string | null;
  vitals: WellnessVitalRow[];
  evidence: Array<{ type: string; source: string; occurred_at: string }>;
  quiet_score: number | null;
  stale_sources?: string[];
  local_date?: string;
  /** When the SERVER computed this response. Always ~now — never a statement
   *  about the data. Do not render this as freshness. */
  as_of: string;
  /** When a source last actually reported. Null = nothing ever has. This is
   *  the one to show a caregiver. */
  data_as_of?: string | null;
  /** Per-source, so "her watch is offline" is distinguishable from "she hasn't
   *  moved" — identical in an aggregate, completely different in meaning. */
  source_last_seen?: {
    health: string | null;
    location: string | null;
    presence: string | null;
    medications: string | null;
  };
};

export type WellnessTimelineEvent = {
  type: string;
  source: string;
  occurred_at: string;
  is_activity: boolean;
  confidence: number;
  actor_is_recipient: boolean;
  detail: Record<string, any>;
};

const ids = () => {
  const { accountId, cognitoId } = getSessionIds();
  return accountId && cognitoId ? { accountId, cognitoId } : null;
};

export const fetchWellnessSnapshot = (projectId: number | string, date?: string) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    const qs = date ? `?date=${encodeURIComponent(date)}` : '';
    return await apiGet(`/wellness/snapshot/${projectId}/${s.accountId}/${s.cognitoId}${qs}`);
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const fetchWellnessTimeline = (
  projectId: number | string,
  opts: { date?: string; days?: number } = {}
) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    const params = new URLSearchParams();
    if (opts.date) params.set('date', opts.date);
    if (opts.days) params.set('days', String(opts.days));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return await apiGet(`/wellness/timeline/${projectId}/${s.accountId}/${s.cognitoId}${qs}`);
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const fetchWellnessSettings = (projectId: number | string) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiGet(`/wellness/settings/${projectId}/${s.accountId}/${s.cognitoId}`);
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const saveWellnessSettings = (projectId: number | string, config: Record<string, any>) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiPost(`/wellness/save-settings/${projectId}/${s.accountId}/${s.cognitoId}`, { config });
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const setupWellness = (projectId: number | string) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiPost(`/wellness/setup/${projectId}/${s.accountId}/${s.cognitoId}`, {});
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/**
 * Turn wellness off entirely (any state -> off) and clear the recipient's
 * consent, so switching back on asks her again rather than silently resuming
 * collection on a months-old agreement.
 */
export const disableWellness = (projectId: number | string) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiPost(`/wellness/disable/${projectId}/${s.accountId}/${s.cognitoId}`, {});
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const consentWellness = (
  projectId: number | string,
  body: {
    tz: string;
    disclosure_version: string;
    /** Set by an account administrator attesting for a recipient who cannot
     *  consent on a device of their own. Requires `attestation`. */
    on_behalf?: boolean;
    attestation?: 'guardian' | 'agreed_in_person';
  }
) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiPost(`/wellness/consent/${projectId}/${s.accountId}/${s.cognitoId}`, body);
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const fetchWellnessIntroStatus = (projectId: number | string) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiGet(`/wellness/intro-status/${projectId}/${s.accountId}/${s.cognitoId}`);
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/** Recipient "I'm OK" ack — emits activity and cancels any pending ladder run (slice 2). */
export const sendImOk = (projectId: number | string) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiPost(`/wellness/im-ok/${projectId}/${s.accountId}/${s.cognitoId}`, {});
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/** Caregiver "dismiss alert" — cancels remaining ladder steps (slice 2). */
export const dismissWellnessAlert = (projectId: number | string) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiPost(`/wellness/dismiss-alert/${projectId}/${s.accountId}/${s.cognitoId}`, {});
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/** Caregiver "check on her now" — silent-pushes the recipient's devices to sync (slice 2). */
export const requestCheckNow = (projectId: number | string) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiPost(`/wellness/check-now/${projectId}/${s.accountId}/${s.cognitoId}`, {});
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/** Device-side completion report for a check-now request (recipient device only). */
export const reportCheck = (
  projectId: number | string,
  body: { request_id: string; status: 'started' | 'completed' | 'failed'; error?: string }
) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiPost(`/wellness/report-check/${projectId}/${s.accountId}/${s.cognitoId}`, body);
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/** Pause/resume = the recipient's own membership pref; the backend transitions state atomically. */
export const setWellnessSharing = (projectId: number | string, enabled: boolean) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    // apiProjectsPatch already targets the projects-service endpoint — no /projects prefix
    // (same shape as ProjectSettingsScreen's membership-pref calls).
    return await apiProjectsPatch(
      `/update-membership-pref/${projectId}/${s.accountId}/${s.cognitoId}`,
      { pref: 'wellness_sharing_enabled', enabled }
    );
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

/**
 * Check-in sheet write path: manual vitals + an optional note, merged into the
 * plan's health store with manual provenance. Server enforces who may record
 * (recipient or wellness viewer) and keeps the activity split honest.
 */
export const recordWellnessCheckIn = (
  projectId: number | string,
  payload: { entries: Array<{ type: string; value: number; unit?: string; at?: string }>; note?: string },
) => async (_dispatch: any) => {
  const s = ids();
  if (!s) return { success: false };
  try {
    return await apiPost(`/wellness/manual-entry/${projectId}/${s.accountId}/${s.cognitoId}`, payload);
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};
